import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Avatar,
  IconButton,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SecurityIcon from '@mui/icons-material/Security';
import GroupIcon from '@mui/icons-material/Group';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StorageIcon from '@mui/icons-material/Storage';

const FarmAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  margin: '0 auto',
  border: `4px solid ${theme.palette.background.paper}`,
  boxShadow: theme.shadows[3],
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
}));

const SettingsSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
}));

interface FarmSettings {
  name: string;
  description: string;
  location: string;
  imageUrl?: string;
  notifications: {
    taskUpdates: boolean;
    workerActivity: boolean;
    reports: boolean;
  };
  privacy: {
    isPublic: boolean;
    showWorkers: boolean;
    showStats: boolean;
  };
  storage: {
    autoBackup: boolean;
    retentionDays: number;
  };
}

interface FarmSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  farmId: string;
  settings: FarmSettings;
  onUpdateSettings: (settings: FarmSettings) => void;
  onUploadImage: (file: File) => Promise<string>;
}

const FarmSettingsDialog: React.FC<FarmSettingsDialogProps> = ({
  open,
  onClose,
  farmId,
  settings,
  onUpdateSettings,
  onUploadImage,
}) => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [localSettings, setLocalSettings] = useState<FarmSettings>(settings);

  /**
   * Handles the farm image upload process
   * @param event - The change event from the file input
   */
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = await onUploadImage(file);
      setLocalSettings({ ...localSettings, imageUrl });
    }
  };

  /**
   * Saves the updated farm settings and closes the dialog
   */
  const handleSave = () => {
    onUpdateSettings(localSettings);
    onClose();
  };

  /**
   * Toggles the expansion state of a settings section
   * @param section - The section identifier to toggle
   */
  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h5" align="center">
          Farm Settings
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          <input
            type="file"
            accept="image/*"
            id="farm-image-upload"
            style={{ display: 'none' }}
            onChange={handleImageUpload}
          />
          <label htmlFor="farm-image-upload">
            <FarmAvatar src={localSettings.imageUrl}>
              <PhotoCameraIcon sx={{ fontSize: 40 }} />
            </FarmAvatar>
          </label>

          <Box sx={{ mt: 4 }}>
            <SettingsSection>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Farm Name"
                    value={localSettings.name}
                    onChange={(e) => setLocalSettings({ ...localSettings, name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={localSettings.description}
                    onChange={(e) => setLocalSettings({ ...localSettings, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Location"
                    value={localSettings.location}
                    onChange={(e) => setLocalSettings({ ...localSettings, location: e.target.value })}
                  />
                </Grid>
              </Grid>
            </SettingsSection>

            <List>
              {/* Notifications Section */}
              <ListItemButton onClick={() => toggleSection('notifications')}>
                <ListItemIcon>
                  <NotificationsIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Notifications"
                  secondary="Configure notification preferences"
                />
                {expandedSection === 'notifications' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
              <Collapse in={expandedSection === 'notifications'}>
                <Box sx={{ p: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localSettings.notifications.taskUpdates}
                        onChange={(e) =>
                          setLocalSettings({
                            ...localSettings,
                            notifications: {
                              ...localSettings.notifications,
                              taskUpdates: e.target.checked,
                            },
                          })
                        }
                      />
                    }
                    label="Task Updates"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localSettings.notifications.workerActivity}
                        onChange={(e) =>
                          setLocalSettings({
                            ...localSettings,
                            notifications: {
                              ...localSettings.notifications,
                              workerActivity: e.target.checked,
                            },
                          })
                        }
                      />
                    }
                    label="Worker Activity"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localSettings.notifications.reports}
                        onChange={(e) =>
                          setLocalSettings({
                            ...localSettings,
                            notifications: {
                              ...localSettings.notifications,
                              reports: e.target.checked,
                            },
                          })
                        }
                      />
                    }
                    label="Reports & Analytics"
                  />
                </Box>
              </Collapse>

              {/* Privacy Section */}
              <ListItemButton onClick={() => toggleSection('privacy')}>
                <ListItemIcon>
                  <SecurityIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Privacy"
                  secondary="Manage farm visibility and data sharing"
                />
                {expandedSection === 'privacy' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
              <Collapse in={expandedSection === 'privacy'}>
                <Box sx={{ p: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localSettings.privacy.isPublic}
                        onChange={(e) =>
                          setLocalSettings({
                            ...localSettings,
                            privacy: {
                              ...localSettings.privacy,
                              isPublic: e.target.checked,
                            },
                          })
                        }
                      />
                    }
                    label="Public Farm Profile"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localSettings.privacy.showWorkers}
                        onChange={(e) =>
                          setLocalSettings({
                            ...localSettings,
                            privacy: {
                              ...localSettings.privacy,
                              showWorkers: e.target.checked,
                            },
                          })
                        }
                      />
                    }
                    label="Show Worker List"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localSettings.privacy.showStats}
                        onChange={(e) =>
                          setLocalSettings({
                            ...localSettings,
                            privacy: {
                              ...localSettings.privacy,
                              showStats: e.target.checked,
                            },
                          })
                        }
                      />
                    }
                    label="Show Farm Statistics"
                  />
                </Box>
              </Collapse>

              {/* Storage Section */}
              <ListItemButton onClick={() => toggleSection('storage')}>
                <ListItemIcon>
                  <StorageIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Storage & Backup"
                  secondary="Configure data retention and backup settings"
                />
                {expandedSection === 'storage' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
              <Collapse in={expandedSection === 'storage'}>
                <Box sx={{ p: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localSettings.storage.autoBackup}
                        onChange={(e) =>
                          setLocalSettings({
                            ...localSettings,
                            storage: {
                              ...localSettings.storage,
                              autoBackup: e.target.checked,
                            },
                          })
                        }
                      />
                    }
                    label="Automatic Backup"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Data Retention (days)"
                    value={localSettings.storage.retentionDays}
                    onChange={(e) =>
                      setLocalSettings({
                        ...localSettings,
                        storage: {
                          ...localSettings.storage,
                          retentionDays: parseInt(e.target.value) || 0,
                        },
                      })
                    }
                    sx={{ mt: 2 }}
                  />
                </Box>
              </Collapse>
            </List>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FarmSettingsDialog;